import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, X, Smile, Meh, Heart, Sparkles, Footprints, Pause, SkipForward, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { JOURNEY } from "@/data/portfolio";
import { track, fetchMoods } from "@/lib/analytics";
import MoodVote from "@/components/site/MoodVote";
import { T, useLang } from "@/lib/i18n";


const W = 1000;
const H = 300;
const PAD_X = 60;
const moodY = (m) => 210 - m * 40; // mood 0..4 → y 210..50
const WALK_SECONDS = 7;

const points = JOURNEY.map((s, i) => ({
  x: PAD_X + (i * (W - PAD_X * 2)) / (JOURNEY.length - 1),
  y: moodY(s.mood),
}));

const path = points
  .map((p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = points[i - 1];
    const cx = (prev.x + p.x) / 2;
    return `C ${cx} ${prev.y}, ${cx} ${p.y}, ${p.x} ${p.y}`;
  })
  .join(" ");

const buildPath = (pts) =>
  pts
    .map((p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = pts[i - 1];
      const cx = (prev.x + p.x) / 2;
      return `C ${cx} ${prev.y}, ${cx} ${p.y}, ${p.x} ${p.y}`;
    })
    .join(" ");

const MoodIcon = ({ mood, className }) =>
  mood >= 4 ? <Heart className={className} /> : mood >= 2 ? <Smile className={className} /> : <Meh className={className} />;

export default function JourneyMap() {
  const { t } = useLang();
  const [active, setActive] = useState(null);
  const [moods, setMoods] = useState({});
  useEffect(() => {
    fetchMoods().then(setMoods).catch(() => {});
  }, []);
  const totalVotes = Object.values(moods).reduce((a, m) => a + m.count, 0);
  const livePoints = JOURNEY.map((s, i) => ({
    x: points[i].x,
    y: moods[s.id]?.count ? moodY(moods[s.id].avg - 1) : points[i].y,
  }));
  const [walking, setWalking] = useState(false);
  const [tick, setTick] = useState(0);
  const walkRef = useRef(false);
  const open = (s) => {
    setTick(0);
    setActive(s);
    track("journey_open", { stage: s.id });
  };
  const go = (to) => {
    stopWalk();
    setActive(null);
    setTimeout(() => document.getElementById(to.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
  };
  const startWalk = () => {
    walkRef.current = true;
    setWalking(true);
    setTick(0);
    setActive(JOURNEY[0]);
    track("journey_walk_start");
  };
  const stopWalk = () => {
    walkRef.current = false;
    setWalking(false);
  };
  const touchX = useRef(null);
  const step = (dir) => {
    const idx = JOURNEY.indexOf(active) + dir;
    if (idx < 0 || idx >= JOURNEY.length) return;
    stopWalk();
    open(JOURNEY[idx]);
    track("journey_swipe", { dir });
  };
  const onTouchStart = (e) => (touchX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 60) step(dx < 0 ? 1 : -1);
  };
  const nextStage = () => {
    const idx = JOURNEY.indexOf(active);
    if (idx >= JOURNEY.length - 1) {
      stopWalk();
      track("journey_walk_complete");
      return;
    }
    setTick(0);
    setActive(JOURNEY[idx + 1]);
  };
  useEffect(() => {
    const h = () => startWalk();
    window.addEventListener("sinthusiast:walk", h);
    return () => window.removeEventListener("sinthusiast:walk", h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!walking || !active) return;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [walking, active]);
  useEffect(() => {
    if (walking && tick >= WALK_SECONDS) nextStage();
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="border-t border-[#e5e0d8] section-pad bg-[#14352a] text-[#faf8f5] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-mono-label text-[11px] text-[#d9a92e]">
              {t("00 · Sinthu Journey map")}
            </div>
          </div>
          <div className="max-w-xs">
            <p className="text-sm text-[#faf8f5]/70 leading-relaxed">
              {t("The site is structured like the artefact I build for a living. The solid curve is the mood I’m aiming for, tap a smiley inside any stage and the dashed line becomes real visitor sentiment.")}
            </p>
            <button
              data-testid="journey-walk-btn"
              onClick={startWalk}
              className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-full btn-guide text-xs font-medium"
            >
              <Footprints className="h-3.5 w-3.5" /> {t("Walk me through the map")}
            </button>
          </div>
        </div>

        {/* Desktop infographic */}
        <div className="hidden md:block mt-6 relative" data-testid="journey-desktop">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" aria-hidden="true">
            <defs>
              <linearGradient id="jg" x1="0" x2="1">
                <stop offset="0" stopColor="#9a7218" />
                <stop offset="1" stopColor="#d9a92e" />
              </linearGradient>
            </defs>
            {[0, 1, 2, 3, 4].map((m) => (
              <line key={m} x1={PAD_X} x2={W - PAD_X} y1={moodY(m)} y2={moodY(m)} stroke="#ffffff22" strokeDasharray="3 6" />
            ))}
            <text x={PAD_X - 8} y={moodY(4) + 4} textAnchor="end" className="fill-[#d9a92e]" fontSize="10" fontFamily="IBM Plex Mono">
              ♥
            </text>
            <text x={PAD_X - 8} y={moodY(0) + 4} textAnchor="end" className="fill-[#d9a92e]" fontSize="10" fontFamily="IBM Plex Mono">
              ?
            </text>
            <motion.path
              d={path}
              fill="none"
              stroke="url(#jg)"
              strokeWidth="3"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1.8, ease: "easeInOut" }}
            />
            {totalVotes > 0 && (
              <motion.path
                data-testid="journey-live-path"
                d={buildPath(livePoints)}
                fill="none"
                stroke="#faf8f5"
                strokeWidth="2"
                strokeDasharray="6 6"
                strokeLinecap="round"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                transition={{ duration: 0.8 }}
              />
            )}
            {points.map((p, i) => (
              <line key={i} x1={p.x} x2={p.x} y1={p.y} y2={H - 60} stroke="#ffffff33" strokeDasharray="2 4" />
            ))}
          </svg>
          {totalVotes > 0 && (
            <div data-testid="journey-legend" className="absolute right-0 top-0 flex gap-4 font-mono-label text-[8px] text-[#faf8f5]/60">
              <span className="inline-flex items-center gap-1.5"><span className="w-5 h-0.5 bg-gradient-to-r from-[#9a7218] to-[#d9a92e]" /> {t("intended")}</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed border-[#faf8f5]" /> {t("live sentiment")} · {totalVotes} {totalVotes === 1 ? "vote" : "votes"}</span>
            </div>
          )}

          {JOURNEY.map((s, i) => {
            const p = points[i];
            return (
              <motion.button
                key={s.id}
                data-testid={`journey-node-${s.id}`}
                onClick={() => open(s)}
                className="absolute group focus:outline-none"
                style={{ left: `${(p.x / W) * 100}%`, top: `${(p.y / H) * 100}%`, transform: "translate(-50%, -50%)" }}
                initial={{ opacity: 0, scale: 0.6 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.25 * i + 0.3, type: "spring", stiffness: 260, damping: 18 }}
                whileHover={{ scale: 1.12 }}
                whileTap={{ scale: 0.95 }}
                aria-label={`${s.stage}: ${s.title}`}
              >
                <span className="relative block h-11 w-11 rounded-full bg-white border-2 border-[#14352a] shadow-md grid place-items-center group-hover:border-[#d9a92e] transition-colors">
                  <MoodIcon mood={s.mood} className="h-5 w-5 text-[#14352a] group-hover:text-[#d9a92e]" />
                </span>
              </motion.button>
            );
          })}

          <div className="absolute inset-x-0" style={{ top: `${((H - 52) / H) * 100}%` }}>
            {JOURNEY.map((s, i) => {
              const p = points[i];
              return (
                <button
                  key={s.id}
                  data-testid={`journey-label-${s.id}`}
                  onClick={() => open(s)}
                  className="absolute -translate-x-1/2 text-center w-[130px] group"
                  style={{ left: `${(p.x / W) * 100}%` }}
                >
                  <div className="font-mono-label text-[9px] text-[#d9a92e]">{t(s.stage)}</div>
                  <div className="mt-1 font-display text-sm leading-tight text-[#faf8f5] group-hover:text-[#d9a92e] transition-colors">
                    {t(s.title)}
                  </div>
                  <div className="mt-0.5 text-[10px] text-[#faf8f5]/55">{t(s.touchpoint)}</div>
                </button>
              );
            })}
          </div>
          <div className="h-16" />
        </div>

        {/* Mobile timeline */}
        <ol className="md:hidden mt-6 relative ml-5 space-y-3" data-testid="journey-mobile">
          <span className="absolute left-[-1px] top-3 bottom-3 w-0.5 bg-gradient-to-b from-[#d9a92e] to-[#faf8f5]/40" />
          {JOURNEY.map((s, i) => {
            const m = moods[s.id];
            return (
              <li key={s.id} className="relative pl-7">
                <span className="absolute -left-[15px] top-3 h-8 w-8 rounded-full bg-white border-2 border-[#14352a] grid place-items-center shadow-sm">
                  <MoodIcon mood={s.mood} className="h-3.5 w-3.5 text-[#14352a]" />
                </span>
                <motion.button
                  data-testid={`journey-node-m-${s.id}`}
                  onClick={() => open(s)}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: 0.05 * i }}
                  whileTap={{ scale: 0.98 }}
                  className="text-left w-full p-4 bg-white border border-[#e5e0d8] active:border-[#9a7218] flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 font-mono-label text-[9px] text-[#9a7218]">
                      <span>{t(s.stage)}</span>
                      {m?.count > 0 && (
                        <span className="ml-auto text-[#86928a]">live {m.avg.toFixed(1)} / 5</span>
                      )}
                    </div>
                    <div className="mt-1 font-display text-lg leading-tight text-[#14352a]">{t(s.title)}</div>
                    <div className="mt-0.5 text-[11px] text-[#86928a]">{t(s.touchpoint)}</div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-[#9a7218]" />
                </motion.button>
              </li>
            );
          })}
        </ol>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { stopWalk(); setActive(null); } }}>
        <DialogContent
          data-testid="journey-dialog"
          className="max-w-lg p-0 overflow-hidden rounded-2xl border-[#e5e0d8] bg-[#faf8f5] [&>button]:hidden"
        >
          {active && (
            <div>
              <div className="p-6 bg-[#14352a] text-[#faf8f5]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-mono-label text-[9px] text-[#d9a92e]">
                      {t(active.stage)}
                    </div>
                    <DialogTitle className="mt-2 font-display text-2xl leading-tight font-medium">
                      {t(active.title)}
                    </DialogTitle>
                    <DialogDescription className="mt-2 text-sm text-[#faf8f5]/75 leading-relaxed">
                      {t(active.blurb)}
                    </DialogDescription>
                  </div>
                  <button
                    data-testid="journey-dialog-close"
                    onClick={() => { stopWalk(); setActive(null); }}
                    className="h-8 w-8 shrink-0 grid place-items-center rounded-full hover:bg-white/10"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="p-6" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                <div className="font-mono-label text-[9px] text-[#86928a]">Touchpoint · {t(active.touchpoint)}</div>
                <ul className="mt-3 space-y-2.5">
                  {active.facts.map((f, i) => (
                    <li key={i} className="flex gap-3 text-sm text-[#14352a] leading-relaxed">
                      <Sparkles className="h-4 w-4 shrink-0 text-[#9a7218] mt-0.5" />
                      {t(f)}
                    </li>
                  ))}
                </ul>
                <MoodVote stage={active.id} stats={moods[active.id]} onMoods={setMoods} onNavigate={() => setActive(null)} />
                {walking && (
                  <div data-testid="journey-walk-bar" className="mt-5 flex items-center gap-3 p-3 bg-[#f6efdc] border border-[#e2c982] rounded-xl">
                    <span className="h-7 w-7 shrink-0 rounded-full btn-guide grid place-items-center"><Footprints className="h-3.5 w-3.5" /></span>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-label text-[8px] text-[#86928a]">{t("Sinthusiast walkthrough")} · {JOURNEY.indexOf(active) + 1} / {JOURNEY.length}</div>
                      <div className="mt-1 h-1 bg-white rounded-full overflow-hidden">
                        <div className="h-full bg-[#9a7218] transition-[width] duration-1000 ease-linear" style={{ width: `${Math.min(100, (tick / WALK_SECONDS) * 100)}%` }} />
                      </div>
                    </div>
                    <button data-testid="journey-walk-next" onClick={nextStage} aria-label="Next stage" className="h-8 w-8 grid place-items-center rounded-full border border-[#14352a] hover:bg-[#14352a] hover:text-white"><SkipForward className="h-3.5 w-3.5" /></button>
                    <button data-testid="journey-walk-stop" onClick={stopWalk} aria-label="Stop walkthrough" className="h-8 w-8 grid place-items-center rounded-full border border-[#14352a] hover:bg-[#14352a] hover:text-white"><Pause className="h-3.5 w-3.5" /></button>
                  </div>
                )}
                <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                  <button
                    data-testid="journey-dialog-cta"
                    onClick={() => go(active.cta.to)}
                    className="h-10 px-5 rounded-full btn-guide text-sm font-medium inline-flex items-center gap-1.5"
                  >
                    {t(active.cta.label)} <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-2">
                    <button data-testid="journey-prev" onClick={() => step(-1)} disabled={JOURNEY.indexOf(active) === 0} aria-label="Previous stage" className="h-8 w-8 grid place-items-center rounded-full border border-[#e5e0d8] disabled:opacity-30 hover:bg-[#f2ece4]"><ChevronLeft className="h-4 w-4" /></button>
                    {JOURNEY.map((s, i) => (
                      <button
                        key={s.id}
                        data-testid={`journey-dialog-dot-${i}`}
                        onClick={() => open(s)}
                        aria-label={s.stage}
                        className={`h-2 rounded-full transition-all ${s.id === active.id ? "w-5 bg-[#d9a92e]" : "w-2 bg-[#d4cebe] hover:bg-[#9a7218]"}`}
                      />
                    ))}
                    <button data-testid="journey-next" onClick={() => step(1)} disabled={JOURNEY.indexOf(active) === JOURNEY.length - 1} aria-label="Next stage" className="h-8 w-8 grid place-items-center rounded-full border border-[#e5e0d8] disabled:opacity-30 hover:bg-[#f2ece4]"><ChevronRight className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
